/**
 * Minimal i18n wrapper.
 *
 * `uz` is the default. `ru` is offered as a personal interface language (spec §5 Ko'rinish,
 * D9.110, superseding 9.1's uz-only list): a person picks it in their profile, and a key the
 * Russian dictionary omits falls back to `uz`, so a missing translation shows the Uzbek text
 * rather than a blank. The choice is remembered on the device so the first paint is already
 * in the person's language; the profile is where it is stored.
 */
import { ru } from "./ru";
import { uz, type TranslationKey } from "./uz";

export type Locale = "uz" | "ru";

export const SUPPORTED_LOCALES: readonly Locale[] = ["uz", "ru"];
export const DEFAULT_LOCALE: Locale = "uz";

const LOCALE_STORAGE_KEY = "kapitan.locale";

function isLocale(value: unknown): value is Locale {
  return value === "uz" || value === "ru";
}

/** The locale remembered on this device, or the default when there is none or no storage. */
function rememberedLocale(): Locale {
  try {
    const value = globalThis.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(value) ? value : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

let currentLocale: Locale = rememberedLocale();

/** The interface language in use now. */
export function activeLocale(): Locale {
  return currentLocale;
}

/**
 * Makes `locale` the interface language and remembers it on this device. Returns whether it
 * changed, so the caller can reload once: strings already on screen are not re-rendered.
 */
export function setLocale(locale: Locale): boolean {
  if (locale === currentLocale) return false;
  currentLocale = locale;
  try {
    globalThis.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // No storage (a private window): the choice lasts for this page only, which is still right.
  }
  return true;
}

const DICTIONARIES: Record<Locale, Partial<Record<TranslationKey, string>>> = { uz, ru };

export type TranslationParams = Record<string, string | number>;

const PLACEHOLDER = /\{(\w+)\}/g;

function interpolate(template: string, params: TranslationParams): string {
  return template.replace(PLACEHOLDER, (_match: string, name: string) => {
    const value = params[name];
    if (value === undefined) {
      throw new Error(`Missing i18n parameter "${name}" for "${template}"`);
    }
    return String(value);
  });
}

/** Translate a key in the active locale. Unknown keys are a compile-time error. */
export function t(
  key: TranslationKey,
  params?: TranslationParams,
  locale: Locale = currentLocale,
): string {
  const template = DICTIONARIES[locale][key] ?? uz[key];
  return params ? interpolate(template, params) : template;
}

export function useTranslation(): {
  t: (key: TranslationKey, params?: TranslationParams) => string;
  locale: Locale;
} {
  return { t, locale: currentLocale };
}

export type { TranslationKey };
